import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import type { PostgrestResponse } from '@supabase/supabase-js';

export interface SourceStalenessDetail {
  source_id: string;
  source_name: string;
  days_since: number | null;
  median_gap: number | null;
  stale_60d: boolean;
  off_cadence: boolean;
  note?: string;
}

interface StalenessRow {
  stale_count: number;
  details: SourceStalenessDetail[];
}

function fetchStalenessRPC(): Promise<PostgrestResponse<StalenessRow>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (supabase as any).rpc('get_source_staleness') as Promise<
    PostgrestResponse<StalenessRow>
  >;
}

export function useSourceStaleness() {
  const [staleCount, setStaleCount] = useState(0);
  const [details, setDetails] = useState<SourceStalenessDetail[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await fetchStalenessRPC();
        if (cancelled) return;
        if (!error && data && data.length > 0) {
          setStaleCount(Number(data[0].stale_count) || 0);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const raw = (data[0] as any).details;
          setDetails(
            Array.isArray(raw) ? (raw as SourceStalenessDetail[]) : []
          );
        } else {
          setStaleCount(0);
          setDetails([]);
        }
      } catch {
        if (!cancelled) {
          setStaleCount(0);
          setDetails([]);
        }
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { staleCount, details, loading, refresh: fetchStalenessRPC };
}
